import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
// Excel 내보내기에 필요한 라이브러리 및 컴포넌트 import
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';

// DevExtreme 컴포넌트 import
import DataGrid, { Column, Paging, FilterRow, Export, Summary, TotalItem, Toolbar, Item } from "devextreme-react/data-grid";
import Chart, { Series, CommonSeriesSettings, ArgumentAxis, ValueAxis, Title, Legend, Tooltip, Font } from 'devextreme-react/chart';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import Button from "devextreme-react/button";
import 'devextreme/dist/css/dx.material.blue.light.css';
import API_URL from '../config';

const CUST_API_URL = `${API_URL}/api/oracle/common/custs`;
const SUMMARY_API_URL = `${API_URL}/api/oracle/wip/shipping-summary`;

const formatDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

// --- 메인 컴포넌트 ---
export default function WipShippingSummary({ user }) {
  // --- 상태 관리 ---
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [factoryList] = useState(['FT01', 'PT01']);
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [custList, setCustList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => new Date(new Date().getFullYear(), 0, 1));
  const [dateTo, setDateTo] = useState(new Date());

  // --- 데이터 패칭 ---
  useEffect(() => {
    if (!selectedFactory) {
      setCustList([]);
      setSelectedCustomer(null);
      return;
    }
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => {
        if (res.data.retCode === 0) {
          setCustList(res.data.data || []);
          setSelectedCustomer(null);
        }
      })
      .catch(e => { console.error("CUST 목록 로드 오류:", e); setCustList([]); });
  }, [selectedFactory]);
  
  const handleSearch = useCallback(async () => {
    if (!selectedFactory || !selectedCustomer) {
      setError("Factory와 Customer를 모두 선택해주세요.");
      return;
    }
    setLoading(true);
    setError("");
    setSummaryData([]);
    try {
      const response = await axios.get(SUMMARY_API_URL, {
        params: { factory: selectedFactory, customer: selectedCustomer, dateFrom: formatDate(dateFrom), dateTo: formatDate(dateTo) }
      });
      if (response.data.retCode === 0) setSummaryData(response.data.data || []);
      else throw new Error(response.data.retMsg);
    } catch (e) {
      setError(`조회 오류: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, selectedCustomer, dateFrom, dateTo]);

  const customizeTooltip = (arg) => ({ text: `${arg.argumentText}<br/>Total Qty: ${arg.valueText}` });

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('WIP_Shipping_Summary');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'WipShippingSummary.xlsx');
      });
    });
    e.cancel = true;
  };

  // --- 렌더링 ---
  return (
    <div style={styles.container}>
      {/* 1. 제어판 */}
      <div style={styles.controlPanel}>
        <SelectBox dataSource={factoryList} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} placeholder="Factory" width={120} />
        <SelectBox dataSource={custList} value={selectedCustomer} onValueChanged={e => setSelectedCustomer(e.value)} valueExpr="CODE_CD" displayExpr="CODE_NM" placeholder="Select Customer" searchEnabled width={200} />
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="From" type="date" displayFormat="yyyy-MM-dd" width={160} />
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} label="To" type="date" displayFormat="yyyy-MM-dd" width={160} />
        <div style={styles.buttonGroup}>
          <Button text="Search" type="default" stylingMode="contained" icon="search" onClick={handleSearch} disabled={loading} height="36px" />
        </div>
      </div>
      
      {error && <div style={styles.errorBox}>{error}</div>}

      {/* 2. 메인 콘텐츠 (차트 + 그리드) */}
      <div style={styles.mainContent}>
        <div style={styles.chartContainer}>
           <Chart id="chart" dataSource={summaryData} height={280}>
                <Export enabled={true} />
                <Title text={`월별 출하 수량 (${selectedCustomer || 'N/A'})`}>
                    <Font size={16} weight={600} />
                </Title>
                <CommonSeriesSettings argumentField="SHIPPING_MONTH" type="bar" />
                <Series valueField="TOTAL_QTY" name="Total Quantity" />
                <ArgumentAxis><Font size={11} /></ArgumentAxis>
                <ValueAxis><Font size={11} /></ValueAxis>
                <Legend visible={false} />
                <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
            </Chart>
        </div>
        <div style={styles.gridContainer}>
           <DataGrid 
                dataSource={summaryData} 
                keyExpr={["FACTORY", "SHIPPING_MONTH", "CUSTOMER_ID"]} 
                height="100%" 
                showBorders 
                columnAutoWidth
                onExporting={onExporting}
            >
              <Toolbar>
                <Item name="exportButton" location="after" />
              </Toolbar>
              <Paging defaultPageSize={15} />
              <FilterRow visible={true} />
              <Column dataField="FACTORY" caption="Factory" alignment="center" width={100} />
              <Column dataField="SHIPPING_MONTH" caption="Shipping Month" alignment="center" />
              <Column dataField="CUSTOMER_ID" caption="Customer" />
              <Column dataField="TOTAL_QTY" caption="Total Quantity" dataType="number" format="#,##0" alignment="right" />
              <Summary>
                <TotalItem column="TOTAL_QTY" summaryType="sum" valueFormat="#,##0" displayFormat="Total: {0}" />
              </Summary>
           </DataGrid>
        </div>
      </div>
       {loading && <div style={styles.loadingOverlay}><div className="loading-spinner"></div></div>}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '80vh',
    padding: '0.75rem',
    boxSizing: 'border-box',
    background: '#f4f5f7',
    fontSize: '13px',
  },
  controlPanel: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: '0.75rem',
  },
  buttonGroup: { marginLeft: 'auto' },
  errorBox: {
    color: "red",
    background: '#ffebee',
    border: '1px solid #e57373',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '12px',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    overflow: 'hidden',
    minHeight: 0,
  },
  chartContainer: {
    flex: '0 0 260px',
    background: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  gridWrapper: {
    flex: 1,
    overflow: 'auto',       // ✅ 스크롤 가능하게
    padding: '0.5rem',
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.85)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
};
